import { getSupplier } from '@bridebook/web/app-basic/lib/supplier/selectors';
import { useSelector } from '@bridebook/web/app-basic/lib/utils';
import Router from 'next/router';
import { memo, useMemo } from 'react';
import gazetteer, { Gazetteer } from '@bridebook/toolbox/src/gazetteer';
import polyglot from '@bridebook/toolbox/src/polyglot';
import { Box, PopoverMenu, PopoverMenuItem, colors } from '@bridebook/ui';
import { IconTranslations } from '@bridebook/ui/src/icons/dynamic';
import { useMarket } from '@bridebook/web/app-shared/lib/i18n/hooks/use-market';
import { usePathWithoutMarket } from '@bridebook/web/lib/utils/url';
import styles from './available-translations.style';

const AvailableTranslationsComponent = () => {
  const market = useMarket();
  const pathname = usePathWithoutMarket();
  const supplier = useSelector(getSupplier);
  const translations = Object.keys(supplier?._translations ?? {});

  const availableTranslations = useMemo(
    () =>
      market.locales
        .filter((locale, index) => {
          if (supplier?.l10n.language != null) {
            /**
             * If the supplier content is provided in a specific language we should resolve against
             * that.
             */
            try {
              return polyglot.resolve(locale) === polyglot.resolve(supplier?.l10n.language);
            } catch (error) {
              return index === 0;
            }
          } else if (index === 0) {
            /**
             * We assume that the supplier content is provided in the market default language.
             */
            return true;
          }

          try {
            /**
             * Do we have translated content for each of the alternative locales?
             */
            return translations.includes(polyglot.resolve(locale));
          } catch (error) {
            return false;
          }
        })
        .map((locale) => {
          const target = gazetteer.getMarketByCountry(market.country);
          target.locale = locale;

          return {
            url: `/${target.prefix}${pathname}`,
            name: Gazetteer.getLanguageName(locale),
            isCurrentMarket: target.prefix === market.prefix,
          };
        }),
    [
      market.country,
      market.locales,
      market.prefix,
      pathname,
      supplier?.l10n.language,
      translations,
    ],
  );

  const onClick = (url: string) => () => Router.push(url, undefined, { locale: false });

  // We only show the translations dropdown if there are more than one available translations
  if (availableTranslations.length <= 1) return null;

  return (
    <>
      <PopoverMenu
        minWidth={200}
        renderTrigger={() => (
          <Box style={styles.trigger}>
            <IconTranslations color={colors.space} />
          </Box>
        )}>
        {availableTranslations.map(({ name, url, isCurrentMarket }) => (
          <PopoverMenuItem
            key={url}
            isSelected={isCurrentMarket}
            onClick={isCurrentMarket ? undefined : onClick(url)}>
            {name}
          </PopoverMenuItem>
        ))}
      </PopoverMenu>

      {/* Display links in DOM for SEO */}
      <Box display="none">
        {availableTranslations
          .filter(({ isCurrentMarket }) => !isCurrentMarket)
          .map(({ name, url }) => (
            <a key={url} href={url}>
              {name}
            </a>
          ))}
      </Box>
    </>
  );
};

export const AvailableTranslations = memo(AvailableTranslationsComponent);
